import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

type ToastVariant = 'success' | 'error' | 'info';
type Toast = { id: number; message: string; variant: ToastVariant };
type ToastApi = { show: (message: string, variant?: ToastVariant) => void; success: (message: string) => void; error: (message: string) => void };

const Context = createContext<ToastApi | null>(null);
const ICONS: Record<ToastVariant, typeof CheckCircle2> = { success: CheckCircle2, error: AlertTriangle, info: Info };
const STYLES: Record<ToastVariant, string> = {
  success: 'border-brand-200 bg-brand-50 text-brand-700',
  error: 'border-red-200 bg-red-50 text-red-700',
  info: 'border-border bg-white text-ink',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((t) => t.id !== id)), []);
  const show = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = ++nextId.current;
    setToasts((current) => [...current, { id, message, variant }]);
    window.setTimeout(() => dismiss(id), 5000);
  }, [dismiss]);
  const value = useMemo<ToastApi>(() => ({ show, success: (m) => show(m, 'success'), error: (m) => show(m, 'error') }), [show]);
  return (
    <Context.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {toasts.map((toast) => {
          const Icon = ICONS[toast.variant];
          return (
            <div key={toast.id} role="status" className={`pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-xl border px-4 py-3 shadow-card ${STYLES[toast.variant]}`}>
              <Icon size={18} className="mt-0.5 shrink-0" />
              <p className="flex-1 text-sm font-medium">{toast.message}</p>
              <button aria-label="Dismiss" className="shrink-0 opacity-60 hover:opacity-100" onClick={() => dismiss(toast.id)}><X size={16} /></button>
            </div>
          );
        })}
      </div>
    </Context.Provider>
  );
}

export function useToast() { const value = useContext(Context); if (!value) throw new Error('useToast must be inside ToastProvider'); return value; }

export function apiErrorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: { error?: string } } })?.response;
  return response?.data?.error ?? fallback;
}
